import {Component, EventEmitter, Output, ChangeDetectionStrategy} from '@angular/core';
import moment from 'moment';
import {Monitor, Unavailability, UNAVAILABILITY_REASONS, UnavailabilityReason, UnavailabilityRequest} from '../../../models/eventRequest';
import {UnavailabilityService} from '../../../_services/unavailability.service';

declare let $: any;

type UnavailabilityMode = 'PERIOD' | 'DAY' | 'SLOT';

/**
 * Saisie d'une indisponibilité de moniteur : période (journées entières), journée ou créneau horaire.
 * Les dates sont envoyées en heure murale UTC, comme les heures de conduite.
 */
@Component({
    selector: 'app-unavailability-form',
    templateUrl: './unavailability-form.component.html',
    styleUrls: ['./unavailability-form.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UnavailabilityFormComponent {

  /** Après création / modification / suppression : message à afficher (créneaux libérés...). */
  @Output() changed = new EventEmitter<string>();

  readonly reasons = Object.entries(UNAVAILABILITY_REASONS)
    .map(([value, r]) => ({value: value as UnavailabilityReason, label: r.icon + ' ' + r.label}));
  readonly hours = Array.from({length: 17}, (_, i) => (i + 6 < 10 ? '0' : '') + (i + 6) + ':00'); // 06:00 → 22:00

  monitors: Monitor[] = [];
  editedId: number | null = null;
  /** Lecture seule : indisponibilité d'un autre moniteur, ou utilisateur lecteur. */
  readOnly = false;

  monitorId: number | null = null;
  mode: UnavailabilityMode = 'DAY';
  date = '';
  dateEnd = '';
  hourStart = '09:00';
  hourEnd = '10:00';
  reason: UnavailabilityReason = 'CONGE';
  comment = '';

  error = '';
  saving = false;
  confirmDelete = false;

  constructor(private unavailabilityService: UnavailabilityService) { }

  /** Nouvelle indisponibilité, pré-remplie sur la date donnée. */
  openNew(monitors: Monitor[], date: string) {
    this.reset(monitors, false);
    this.monitorId = monitors.length === 1 ? monitors[0].id : null;
    this.date = date;
    this.dateEnd = date;
    $('#unavailabilityModal').modal('show');
  }

  /** Consultation / modification d'une indisponibilité existante. */
  openExisting(unavailability: Unavailability, monitors: Monitor[], readOnly: boolean) {
    this.reset(monitors, readOnly);
    this.editedId = unavailability.id;
    this.monitorId = unavailability.monitorId;
    if (!this.monitors.some(m => m.id === unavailability.monitorId)) {
      this.monitors = [new Monitor(unavailability.monitorId, unavailability.monitorName, '', '', ''), ...this.monitors];
    }
    const start = moment.utc(unavailability.startAt);
    const end = moment.utc(unavailability.endAt);
    this.date = start.format('YYYY-MM-DD');
    if (unavailability.allDay) {
      const lastDay = end.clone().subtract(1, 'day');
      this.mode = lastDay.isSame(start, 'day') ? 'DAY' : 'PERIOD';
      this.dateEnd = lastDay.format('YYYY-MM-DD');
    } else {
      this.mode = 'SLOT';
      this.dateEnd = this.date;
      this.hourStart = start.format('HH:mm');
      this.hourEnd = end.format('HH:mm');
    }
    this.reason = unavailability.reason;
    this.comment = unavailability.comment || '';
    $('#unavailabilityModal').modal('show');
  }

  save() {
    const request = this.toRequest();
    if (!request) {
      return;
    }
    this.saving = true;
    this.error = '';
    const call = this.editedId === null
      ? this.unavailabilityService.create(request)
      : this.unavailabilityService.update(this.editedId, request);
    call.subscribe(
      saved => {
        this.saving = false;
        $('#unavailabilityModal').modal('hide');
        const removed = saved.removedSlots || 0;
        this.changed.emit(removed > 0 ? `${removed} créneau(x) libre(s) supprimé(s) sur la période.` : '');
      },
      err => {
        this.saving = false;
        this.error = err.error?.message || 'Enregistrement impossible';
      }
    );
  }

  remove() {
    if (this.editedId === null) {
      return;
    }
    this.unavailabilityService.delete(this.editedId).subscribe(
      () => {
        $('#unavailabilityModal').modal('hide');
        this.changed.emit('');
      },
      err => this.error = err.error?.message || 'Suppression impossible'
    );
  }

  /** Période : journées entières [début, fin + 1 jour[ ; journée : [date, date + 1[ ; créneau : [date heure début, date heure fin[. */
  private toRequest(): UnavailabilityRequest | null {
    if (this.monitorId == null || !this.date) {
      this.error = 'Merci de choisir un moniteur et une date';
      return null;
    }
    let start: moment.Moment;
    let end: moment.Moment;
    if (this.mode === 'SLOT') {
      start = moment.utc(this.date + 'T' + this.hourStart);
      end = moment.utc(this.date + 'T' + this.hourEnd);
    } else {
      start = moment.utc(this.date);
      end = moment.utc(this.mode === 'PERIOD' ? this.dateEnd : this.date).add(1, 'day');
    }
    if (!end.isAfter(start)) {
      this.error = 'La fin doit être après le début';
      return null;
    }
    return {
      monitorId: this.monitorId,
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      allDay: this.mode !== 'SLOT',
      reason: this.reason,
      comment: this.comment || undefined
    };
  }

  private reset(monitors: Monitor[], readOnly: boolean) {
    this.monitors = monitors;
    this.readOnly = readOnly;
    this.editedId = null;
    this.mode = 'DAY';
    this.hourStart = '09:00';
    this.hourEnd = '10:00';
    this.reason = 'CONGE';
    this.comment = '';
    this.error = '';
    this.saving = false;
    this.confirmDelete = false;
  }
}
